// Grilla de líneas de la nota (comercial ronda 1): # · Código · Producto · Stock · Cantidad · P. lista
// · P. venta · Desc. · Total. KEYBOARD FIRST con dos modos:
//  - NAVEGACIÓN (por defecto): las flechas mueven la celda activa por toda la grilla sin editar ni
//    abrir nada; Enter entra a editar; Supr quita la línea (con confirmación); escribir un número en
//    Cantidad o Desc. empieza a editar con ese número.
//  - EDICIÓN: Enter confirma y avanza al siguiente campo EDITABLE (cantidad → precio venta, si el
//    usuario puede → descuento → código de la línea siguiente); Esc sale sin perder la línea.
// Toda línea se registra en UNIDAD BASE: «5c» con RTU 9 se guarda como 45 UN (la caja es ayuda de
// digitación, ./cantidad.ts). Si el producto tiene caja, la cantidad se digita en el diálogo
// «Cantidad para unidades distintas» (./CantidadRtu.tsx, como Random): unidades o cajas. Precio, escala, impuestos y totales los calcula el BACKEND
// (previsualización) y llegan solos: nunca se estiman aquí.
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type FocusEvent,
  type KeyboardEvent,
} from 'react';
import { usePrecioReferencia, useProducto } from '../api/consultas';
import type {
  DisponibilidadPedida,
  FilaDisponibilidad,
  LineaNota,
  LineaReserva,
} from '../api/contratos';
import { Modal } from '../comun/Modal';
import { STOCK_POR_REVISAR, decimal, esCero, pesos } from '../comun/formato';
import type { LineaBorrador } from './borrador';
import { CantidadRtu } from './CantidadRtu';
import { leerCantidad, problemaMultiplo, secundarias, type Equivalencia } from './cantidad';

export interface GrillaLineasRef {
  /** Foco a la cantidad de una línea, EDITANDO (la recién agregada o la activa). */
  enfocarCantidad(clave?: string): void;
  enfocarAgregar(): void;
  claveActiva(): string | null;
}

interface Props {
  empresaId: string;
  listaId: string | null;
  /** Código de cada lista (para marcar la línea digitada con otra lista que la de la nota). */
  codigoLista?: (id: string) => string | null;
  lineas: LineaBorrador[];
  /** Lo último que calculó el backend, en el mismo orden en que se envió. */
  calculadas: LineaNota[] | null;
  reservas: LineaReserva[] | null;
  obsoleto: boolean;
  soloLectura: boolean;
  /** Se puede agregar líneas (hay cliente y la nota es editable). */
  puedeAgregar: boolean;
  puedePrecioManual: boolean;
  /** Punto de venta: Tab (sin Shift) en la grilla pasa directo al medio de pago (2026-10-23). */
  alTabulador?: () => void;
  /** Tab en la fila de agregar (la última): salir de la grilla hacia «Confirmar». */
  alTabularFinal?: () => void;
  /**
   * Quien digita no tiene el permiso ventas.precio.manual: igual puede ponerlo, pero la nota pide
   * autorización en línea al confirmar (pedido de Rodrigo, 2026-10-22).
   */
  precioManualConAutorizacion?: boolean;
  disponibilidad: FilaDisponibilidad[] | undefined;
  /** Lo pedido contra lo disponible, decidido por el backend al previsualizar. */
  pedido?: DisponibilidadPedida[] | null;
  /** Texto de la fila de digitación (controlado por la nota). */
  textoAgregar: string;
  alCambiarTextoAgregar: (t: string) => void;
  /** Enter en la fila de digitación: la nota resuelve el código exacto o abre el buscador. */
  alBuscarProducto: (texto: string) => void;
  alCambiar: (clave: string, campos: Partial<LineaBorrador>) => void;
  alQuitar: (clave: string) => void;
  /** La fila activa cambió (no abre nada: el panel Producto se abre solo si se pide). */
  alSeleccionar?: (clave: string) => void;
  /** Pedido EXPLÍCITO de ver el producto (clic en el nombre o Enter sobre él). */
  alVerProducto?: (clave: string) => void;
  claveSeleccionada?: string | null;
}

type Col = 'codigo' | 'producto' | 'stock' | 'cantidad' | 'lista' | 'venta' | 'descuento' | 'total';
const COLS: Col[] = [
  'codigo',
  'producto',
  'stock',
  'cantidad',
  'lista',
  'venta',
  'descuento',
  'total',
];

function textoDescuento(d: LineaBorrador['descuento']): string {
  if (!d) return '';
  return d.tipo === 'PORCENTAJE' ? `${d.valor}%` : d.valor;
}
function leerDescuento(t: string): LineaBorrador['descuento'] {
  const v = t.trim();
  if (!v) return null;
  return v.endsWith('%')
    ? { tipo: 'PORCENTAJE', valor: v.slice(0, -1).trim() }
    : { tipo: 'MONTO', valor: v };
}
const esNumeroSimple = (t: string) => /^\s*\d+(?:[.,]\d*)?\s*$/.test(t);

/** Precio de LISTA de la presentación (sin escala): referencia que no decide el cobro. */
function PrecioLista({
  empresaId,
  listaId,
  linea,
  calculada,
  clase,
  celda,
  codigoLista,
}: {
  empresaId: string;
  listaId: string | null;
  linea: LineaBorrador;
  calculada: LineaNota | null;
  clase: string;
  celda: Record<string, unknown>;
  codigoLista?: ((id: string) => string | null) | undefined;
}) {
  // La línea se precia con SU lista si la tiene (se digitó antes de cambiar la de la nota).
  const propia =
    linea.listaPrecioId && linea.listaPrecioId !== listaId ? linea.listaPrecioId : null;
  const q = usePrecioReferencia(
    empresaId,
    propia ?? listaId,
    linea.productoId,
    linea.presentacionId,
  );
  // Sin escala, el automático de la previsualización ES el de lista.
  const valor =
    q.data?.valor ?? (calculada && !calculada.escalaDesde ? calculada.precioLista : null);
  return (
    <td
      {...celda}
      className={`al-der mono ${clase} ${calculada?.escalaDesde ? 'tachable' : ''}`}
      title={
        propia
          ? `Precio de la lista ${codigoLista?.(propia) ?? ''} (la línea se digitó con otra lista que la de la nota)`
          : 'Precio de lista (solo lectura)'
      }
    >
      {propia && (
        <span className="insignia tono-aviso" data-lista-linea={propia}>
          {codigoLista?.(propia) ?? 'otra lista'}
        </span>
      )}{' '}
      {valor ? pesos(valor) : ''}
    </td>
  );
}

function PrecioManualDialogo({
  linea,
  precioInicial,
  conAutorizacion,
  alGuardar,
  alCerrar,
}: {
  linea: LineaBorrador;
  precioInicial?: string;
  conAutorizacion?: boolean;
  alGuardar: (v: LineaBorrador['precioManual']) => void;
  alCerrar: () => void;
}) {
  const [precio, setPrecio] = useState(precioInicial ?? linea.precioManual?.precio ?? '');
  const [motivo, setMotivo] = useState(linea.precioManual?.motivo ?? '');
  // Sin pedir motivo (Rodrigo, 2026-10-25: «solo trazable»): queda quién, cuándo, el precio de lista
  // y el manual; el comentario es opcional.
  const valido = !!precio.trim();
  const aplicar = () =>
    alGuardar({ precio, motivo: motivo.trim().length >= 3 ? motivo.trim() : 'Precio manual' });
  return (
    <Modal
      titulo={`Precio manual · ${linea.sku}`}
      alCerrar={alCerrar}
      ancho="angosto"
      pie={
        <>
          {linea.precioManual && (
            <button type="button" onClick={() => alGuardar(null)}>
              Quitar precio manual
            </button>
          )}
          <button type="button" className="primario" disabled={!valido} onClick={aplicar}>
            Aplicar
          </button>
        </>
      }
    >
      <p className="ayuda">
        Precio BRUTO por unidad. Queda registrado como MANUAL, con el precio de lista como
        referencia y quién lo puso. Nunca puede quedar bajo el costo del producto.
      </p>
      {conAutorizacion && (
        <p className="aviso-autorizacion" role="note">
          No tienes el permiso de precio manual: al confirmar la nota se pide la autorización en
          línea a quien puede firmarlo.
        </p>
      )}
      <label className="campo">
        Precio bruto
        <input
          data-autofoco
          inputMode="decimal"
          value={precio}
          onChange={(e) => setPrecio(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && valido) {
              e.preventDefault();
              aplicar();
            }
          }}
        />
      </label>
      <label className="campo">
        Comentario (opcional)
        <input
          value={motivo === 'Precio manual' ? '' : motivo}
          onChange={(e) => setMotivo(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && valido) {
              e.preventDefault();
              aplicar();
            }
          }}
        />
      </label>
    </Modal>
  );
}

/** Filas vacías de relleno: la grilla se ve como hoja de trabajo aunque tenga pocas líneas. */
const RELLENO = 40;

interface PropsFila {
  p: Props;
  l: LineaBorrador;
  i: number;
  activa: boolean;
  editando: Col | null;
  celda: (col: Col) => Record<string, unknown>;
  /** El campo tomó el foco (clic, F4, Enter): esa celda está en EDICIÓN. */
  alEditar: (col: Col) => void;
  /** El campo de esa columna perdió el foco: deja de editarse (si sigue siendo el que se edita). */
  alSoltar: (col: Col) => void;
  /** Esc: vuelve a NAVEGACIÓN sobre la misma celda; la línea queda intacta. */
  alSalir: (col: Col) => void;
  /** ↑ ↓ en edición: confirma y navega a la fila vecina. */
  alMover: (col: Col, df: number) => void;
  /** Enter confirmó el campo: la grilla avanza al siguiente editable. */
  alConfirmar: (col: Col) => void;
  alPrecioManual: (precio?: string) => void;
}

function FilaLinea({
  p,
  l,
  i,
  activa,
  editando,
  celda,
  alEditar,
  alSoltar,
  alSalir,
  alMover,
  alConfirmar,
  alPrecioManual,
}: PropsFila) {
  const producto = useProducto(p.empresaId, l.productoId);
  const unidad = producto.data?.producto.unidad_base_codigo ?? 'UN';
  const multiplo = producto.data?.producto.venta_multiplo ?? null;
  const cajas = secundarias(producto.data?.presentaciones ?? []);
  // Producto con caja: la cantidad se digita en el diálogo RTU (unidades o cajas), como en Random.
  const conRtu = !p.soloLectura && cajas.length > 0;
  const [texto, setTexto] = useState(l.cantidad);
  const [error, setError] = useState<string | null>(null);
  const [desc, setDesc] = useState(textoDescuento(l.descuento));
  const [venta, setVenta] = useState('');
  // Lo guardado manda mientras no se edita (otra pestaña, una revisión, un cambio desde el panel).
  useEffect(() => {
    if (editando !== 'cantidad') setTexto(l.cantidad);
  }, [l.cantidad, editando]);
  useEffect(() => {
    if (editando !== 'descuento') setDesc(textoDescuento(l.descuento));
  }, [l.descuento, editando]);

  const c = (() => {
    const x = p.calculadas?.[i];
    return x && x.productoId === l.productoId && x.presentacionId === l.presentacionId ? x : null;
  })();
  const precioActual = l.precioManual?.precio ?? (c ? String(Number(c.precioAplicado)) : '');
  // Al entrar a editar el precio venta, el campo parte con el precio vigente.
  useEffect(() => {
    if (editando === 'venta') setVenta(precioActual);
  }, [editando]);
  const d = p.disponibilidad?.find((f) => f.productoId === l.productoId) ?? null;
  const ped = p.pedido?.find((f) => f.productoId === l.productoId) ?? null;
  const r = p.reservas?.find((x) => x.numero === i + 1) ?? null;
  const clase = [c && p.obsoleto ? 'obsoleto' : '', !c ? 'sin-calcular' : '']
    .filter(Boolean)
    .join(' ');
  const falta = ped ? !ped.alcanza : false;

  // Stock insuficiente AL DIGITAR (pedido de Rodrigo, 2026-10-23, como Random): una cantidad mayor
  // a lo disponible en la bodega de la nota avisa al tiro en vez de enterarse al confirmar. Cuenta
  // las otras líneas del mismo producto. Desde 2026-10-30 avisa pero deja «Ingresar igual»: la nota
  // (o el documento) se guarda como borrador para revisar el stock; confirmar o emitir lo sigue exigiendo.
  const [sinStock, setSinStock] = useState<{
    base: string;
    pedido: number;
    cabe: number;
    equivalencia: Equivalencia | null;
  } | null>(null);
  const avisando = useRef(false);
  // Cantidad que el ejecutivo decidió ingresar igual: por esa no se vuelve a avisar.
  const ingresadaSinStock = useRef<number | null>(null);
  const excede = (base: string): { pedido: number; cabe: number } | null => {
    if (p.soloLectura || !p.disponibilidad) return null;
    const pedido = Number(base);
    if (!(pedido > 0) || pedido === ingresadaSinStock.current) return null;
    const otras = p.lineas
      .filter((x) => x.productoId === l.productoId && x.clave !== l.clave)
      .reduce((a, x) => a + (Number(x.cantidad) || 0), 0);
    const cabe = Math.max(0, Number(d?.disponible ?? 0) - otras);
    return pedido > cabe ? { pedido, cabe } : null;
  };
  const avisarSiExcede = (base: string, equivalencia: Equivalencia | null = null): boolean => {
    const x = excede(base);
    if (!x) return false;
    avisando.current = true;
    setSinStock({ ...x, base, equivalencia });
    return true;
  };
  const cerrarAviso = (cantidad: string | null, equivalencia: Equivalencia | null = null) => {
    setSinStock(null);
    avisando.current = false;
    if (cantidad === null) return; // corregir: el foco vuelve a la cantidad
    setTexto(cantidad);
    p.alCambiar(l.clave, { cantidad, equivalencia });
    setTimeout(() => alConfirmar('cantidad'), 0);
  };
  const ingresarIgual = () => {
    if (!sinStock) return;
    ingresadaSinStock.current = sinStock.pedido;
    cerrarAviso(sinStock.base, sinStock.equivalencia);
  };

  /** Confirma la cantidad digitada: unidad base, o RTU («5c»), respetando el envase cerrado. */
  const confirmarCantidad = (): boolean => {
    const leida = leerCantidad(texto, producto.data?.presentaciones ?? [], unidad);
    if (!leida.ok) {
      setError(leida.error);
      return false;
    }
    const m = problemaMultiplo(leida.base, multiplo);
    if (m) {
      setError(m);
      return false;
    }
    setError(null);
    setTexto(leida.base);
    p.alCambiar(l.clave, { cantidad: leida.base, equivalencia: leida.equivalencia });
    return !avisarSiExcede(leida.base, leida.equivalencia);
  };

  const teclaEdicion = (e: KeyboardEvent<HTMLInputElement>, col: Col, confirmar: () => boolean) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      if (confirmar()) alConfirmar(col);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      if (col === 'cantidad') {
        setTexto(l.cantidad);
        setError(null);
      }
      if (col === 'descuento') setDesc(textoDescuento(l.descuento));
      alSalir(col);
    } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
      e.stopPropagation();
      if (confirmar()) alMover(col, e.key === 'ArrowUp' ? -1 : 1);
    }
  };

  const confirmarDescuento = () => {
    p.alCambiar(l.clave, { descuento: leerDescuento(desc) });
    return true;
  };
  const confirmarVenta = () => {
    if (!venta.trim() || venta.trim() === precioActual) return true;
    alPrecioManual(venta.trim());
    return false;
  };

  const editable = !p.soloLectura;
  return (
    <tr
      data-linea={i + 1}
      className={activa ? 'activa' : ''}
      onClick={() => p.alSeleccionar?.(l.clave)}
    >
      <td className="mono apagado celda-n">
        <span>{i + 1}</span>
        {editable && (
          <button
            type="button"
            className="quitar"
            tabIndex={-1}
            title="Quitar línea (Supr)"
            aria-label={`Quitar línea ${i + 1}`}
            onClick={() => p.alQuitar(l.clave)}
          >
            ✕
          </button>
        )}
      </td>
      <td {...celda('codigo')} className="mono">
        {l.sku}
      </td>
      <td
        {...celda('producto')}
        className="producto"
        title={`${l.nombre} · clic o Enter: ver el producto`}
        onClick={() => p.alVerProducto?.(l.clave)}
      >
        {l.nombre}
      </td>
      <td
        {...celda('stock')}
        className={`al-der mono ${falta ? 'texto-aviso fuerte' : ''}`}
        title={[
          ped
            ? `Pedido del producto ${decimal(ped.requerido)} · disponible ${decimal(ped.disponible)} (${unidad})${falta ? ' · stock insuficiente' : ''}`
            : `Disponible en la bodega de la nota (${unidad})`,
          d?.stockPorRevisar ? STOCK_POR_REVISAR : null,
        ]
          .filter(Boolean)
          .join(' · ')}
        data-alcanza={ped ? String(ped.alcanza) : undefined}
        data-stock-por-revisar={d?.stockPorRevisar ? 'true' : undefined}
      >
        {d ? decimal(d.disponible) : '0'}
        {falta && ' ⚠'}
        {d?.stockPorRevisar && (
          <span className="insignia tono-aviso" aria-label={STOCK_POR_REVISAR}>
            revisar
          </span>
        )}
      </td>
      <td
        {...celda('cantidad')}
        className={`al-der celda-cantidad ${editando === 'cantidad' ? 'editando' : ''}`}
      >
        {p.soloLectura ? (
          <span className="mono fuerte">{decimal(l.cantidad)}</span>
        ) : (
          <span className="cantidad-con-unidad">
            <input
              className="celda-num fuerte"
              inputMode="decimal"
              tabIndex={-1}
              aria-label={`Cantidad línea ${i + 1}`}
              aria-invalid={error ? true : undefined}
              data-cantidad={l.clave}
              value={texto}
              onFocus={() => alEditar('cantidad')}
              onChange={(e) => {
                const t = e.target.value;
                setTexto(t);
                setError(null);
                // Con caja, lo digitado sobre la celda solo abre el diálogo con ese inicio.
                if (conRtu) return;
                // Un número solo es unidad base: se calcula al tiro (sin esperar Enter), salvo que
                // rompa el envase cerrado. Con unidad («5c») se convierte al confirmar.
                if (esNumeroSimple(t) && Number(t.replace(',', '.')) > 0) {
                  const base = t.trim().replace(',', '.');
                  const m = problemaMultiplo(base, multiplo);
                  if (m) setError(m);
                  else p.alCambiar(l.clave, { cantidad: t.trim(), equivalencia: null });
                }
              }}
              onBlur={() => {
                if (conRtu || avisando.current) return; // el diálogo RTU o el aviso deciden
                if (texto === l.cantidad && avisarSiExcede(l.cantidad)) return;
                if (texto !== l.cantidad && !confirmarCantidad()) {
                  setTexto(l.cantidad);
                  setError(null);
                }
                alSoltar('cantidad');
              }}
              onKeyDown={(e) => teclaEdicion(e, 'cantidad', confirmarCantidad)}
            />
            <span className="unidad" title={`Unidad base: ${unidad}`}>
              {unidad}
            </span>
          </span>
        )}
        {(error || (editando === 'cantidad' && cajas.length > 0) || multiplo) && (
          <span
            className={`ayuda-cantidad ${error ? 'texto-malo' : ''}`}
            data-ayuda-cantidad
            role={error ? 'alert' : undefined}
          >
            {error ??
              (editando === 'cantidad' && cajas[0]
                ? `o ${cajas[0].codigo.slice(0, 1).toLowerCase()}: 1 ${cajas[0].codigo} = ${decimal(cajas[0].factor_a_base)} ${unidad}`
                : `envase ×${decimal(multiplo)}`)}
          </span>
        )}
        {conRtu && editando === 'cantidad' && producto.data && (
          <CantidadRtu
            sku={l.sku}
            nombre={l.nombre}
            unidad={unidad}
            presentaciones={producto.data.presentaciones}
            cajas={cajas}
            inicial={texto}
            reemplazar={texto !== l.cantidad}
            multiplo={multiplo}
            disponible={d ? d.disponible : null}
            alAceptar={(base, equivalencia) => {
              setTexto(base);
              setError(null);
              // Más que el disponible: el aviso queda sobre el diálogo RTU y decide.
              if (avisarSiExcede(base, equivalencia)) return;
              p.alCambiar(l.clave, { cantidad: base, equivalencia });
              // Después de cerrar el diálogo: el siguiente campo editable (como Enter en la celda).
              setTimeout(() => alConfirmar('cantidad'), 0);
            }}
            alCancelar={() => {
              setTexto(l.cantidad);
              alSalir('cantidad');
            }}
          />
        )}
        {sinStock && (
          <Modal
            titulo={`Stock insuficiente · ${l.sku}`}
            ancho="angosto"
            ubicacion="media"
            alCerrar={() => cerrarAviso(null)}
            pie={
              <>
                {sinStock.cabe > 0 ? (
                  <>
                    <button type="button" onClick={() => cerrarAviso(null)}>
                      Corregir cantidad
                    </button>
                    <button type="button" onClick={() => cerrarAviso(String(sinStock.cabe))}>
                      Dejar {decimal(String(sinStock.cabe))} {unidad} (lo disponible)
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        cerrarAviso(null);
                        p.alQuitar(l.clave);
                      }}
                    >
                      Quitar la línea
                    </button>
                    <button type="button" onClick={() => cerrarAviso(null)}>
                      Corregir cantidad
                    </button>
                  </>
                )}
                <button type="button" className="primario" data-autofoco onClick={ingresarIgual}>
                  Ingresar igual <kbd>Enter</kbd>
                </button>
              </>
            }
          >
            <p role="alert" data-sin-stock={l.clave}>
              <b>{l.nombre}</b>: pediste {decimal(String(sinStock.pedido))} {unidad} y{' '}
              {sinStock.cabe > 0
                ? `solo hay ${decimal(String(sinStock.cabe))} ${unidad} disponibles`
                : 'no hay stock disponible'}{' '}
              en la bodega de la nota.
            </p>
            <p className="apagado">
              Si la ingresas igual, se guarda como borrador para revisar el stock: no se confirma
              ni se emite mientras falte.
            </p>
          </Modal>
        )}
        {r && !esCero(r.pendiente) && (
          <span className="pend" title="Reserva parcial: pendiente en unidad base">
            pend. {decimal(r.pendiente)}
          </span>
        )}
      </td>
      <PrecioLista
        empresaId={p.empresaId}
        listaId={p.listaId}
        codigoLista={p.codigoLista}
        linea={l}
        calculada={c}
        clase={clase}
        celda={celda('lista')}
      />
      <td
        {...celda('venta')}
        className={`al-der ${clase} ${editando === 'venta' ? 'editando' : ''}`}
        data-origen={c?.origenPrecio}
        title={
          c?.escalaDesde
            ? `Tramo desde ${decimal(c.escalaDesde)}${c.escalaHasta ? ` hasta ${decimal(c.escalaHasta)}` : ''} (u. base; acumulado del producto ${decimal(c.cantidadBaseProducto)}) a ${pesos(c.precioUnitarioBase)} por unidad base`
            : p.puedePrecioManual && editable
              ? 'Precio venta: Enter para cambiarlo (precio manual con motivo)'
              : 'Precio venta (lo calcula el backend)'
        }
      >
        {editando === 'venta' ? (
          <input
            className="celda-num"
            inputMode="decimal"
            aria-label={`Precio venta línea ${i + 1}`}
            value={venta}
            onChange={(e) => setVenta(e.target.value)}
            onBlur={() => alSoltar('venta')}
            onKeyDown={(e) => teclaEdicion(e, 'venta', confirmarVenta)}
            ref={(el) => el?.focus()}
          />
        ) : c ? (
          <span className="precio-venta">
            {c.escalaDesde && <span className="escala-tramo">≥{decimal(c.escalaDesde)}</span>}
            {c.origenPrecio === 'MANUAL' && (
              <span
                className="origen-manual"
                title={`Precio manual (D-60)${c.precioReferencia ? ` · referencia ${pesos(c.precioReferencia)}` : ''}`}
              >
                M
              </span>
            )}
            <span className="mono fuerte">{pesos(c.precioAplicado)}</span>
          </span>
        ) : l.precioManual ? (
          <span className="apagado">manual {l.precioManual.precio}</span>
        ) : (
          <span className="pendiente-calculo">…</span>
        )}
        {p.puedePrecioManual && editable && editando !== 'venta' && (
          <button
            type="button"
            className="boton-celda"
            tabIndex={-1}
            title="Precio manual (D-60)"
            aria-label={`Precio manual línea ${i + 1}`}
            onClick={() => alPrecioManual()}
          >
            $
          </button>
        )}
      </td>
      <td
        {...celda('descuento')}
        className={`al-der ${editando === 'descuento' ? 'editando' : ''}`}
      >
        {p.soloLectura ? (
          <span className="mono">{c && !esCero(c.descuento) ? pesos(c.descuento) : ''}</span>
        ) : (
          <input
            className="celda-num"
            tabIndex={-1}
            aria-label={`Descuento línea ${i + 1}`}
            title="Monto en pesos, o porcentaje con % (ej. 5%)"
            placeholder="0"
            value={desc}
            onFocus={() => alEditar('descuento')}
            onChange={(e) => setDesc(e.target.value)}
            onBlur={() => {
              confirmarDescuento();
              alSoltar('descuento');
            }}
            onKeyDown={(e) => teclaEdicion(e, 'descuento', confirmarDescuento)}
          />
        )}
      </td>
      <td {...celda('total')} className={`al-der mono fuerte ${clase}`}>
        {c ? (
          c.precioAplicado ? (
            pesos(c.bruto)
          ) : (
            <span className="sin-precio">SIN PRECIO</span>
          )
        ) : (
          ''
        )}
      </td>
    </tr>
  );
}

export const GrillaLineas = forwardRef<GrillaLineasRef, Props>(function GrillaLineas(p, ref) {
  const cuerpo = useRef<HTMLTableSectionElement>(null);
  const agregar = useRef<HTMLInputElement>(null);
  const activa = useRef<string | null>(null);
  const [cursor, setCursor] = useState<{ fila: number; col: Col }>({ fila: 0, col: 'cantidad' });
  const [editando, setEditando] = useState<{ fila: number; col: Col } | null>(null);
  const [manual, setManual] = useState<{ linea: LineaBorrador; precio?: string } | null>(null);
  const editable = !p.soloLectura;
  const editables: Col[] = editable
    ? // Sin el permiso, Enter salta el precio (venta rápida); el botón $ sigue disponible.
      [
        'cantidad',
        ...(p.puedePrecioManual && !p.precioManualConAutorizacion ? (['venta'] as Col[]) : []),
        'descuento',
      ]
    : [];

  const td = (fila: number, col: Col) =>
    cuerpo.current?.querySelector<HTMLTableCellElement>(`[data-celda="${fila}:${col}"]`) ?? null;

  /** Navegación: la celda recibe el foco SIN editar. */
  const ir = (fila: number, col: Col) => {
    const f = Math.max(0, Math.min(fila, p.lineas.length));
    if (f === p.lineas.length) {
      setEditando(null);
      agregar.current?.focus();
      return;
    }
    setCursor({ fila: f, col });
    setEditando(null);
    seleccionar(p.lineas[f]!.clave);
    td(f, col)?.focus();
  };

  /** Edición: el campo de la celda recibe el foco y se selecciona su contenido. */
  const editar = (fila: number, col: Col, inicial?: string) => {
    const l = p.lineas[fila];
    if (!l || !editables.includes(col)) return;
    setCursor({ fila, col });
    setEditando({ fila, col });
    seleccionar(l.clave);
    if (col === 'venta') return; // el campo aparece al entrar en edición (y toma el foco solo)
    const input = td(fila, col)?.querySelector<HTMLInputElement>('input');
    if (!input) return;
    input.focus();
    if (inicial !== undefined) {
      // Escribir sobre la celda en navegación reemplaza el valor (como una planilla).
      const setter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set;
      setter?.call(input, inicial);
      input.dispatchEvent(new Event('input', { bubbles: true }));
    } else input.select();
  };

  const seleccionar = (clave: string) => {
    activa.current = clave;
    p.alSeleccionar?.(clave);
  };

  useImperativeHandle(ref, () => ({
    enfocarCantidad: (clave?: string) => {
      const k = clave ?? activa.current ?? p.lineas[p.lineas.length - 1]?.clave;
      const i = p.lineas.findIndex((l) => l.clave === k);
      if (i >= 0) editar(i, 'cantidad');
    },
    enfocarAgregar: () => {
      setEditando(null);
      agregar.current?.focus();
    },
    claveActiva: () => activa.current,
  }));

  /** Enter confirmó un campo: el siguiente EDITABLE de la línea; al final, el código siguiente. */
  const avanzar = (fila: number, col: Col) => {
    const resto = editables.slice(editables.indexOf(col) + 1);
    if (resto[0]) editar(fila, resto[0]);
    else {
      setEditando(null);
      agregar.current?.focus();
    }
  };

  /** Teclas de NAVEGACIÓN (el foco está en una celda, no en un campo). */
  const teclaNavegacion = (e: KeyboardEvent<HTMLTableSectionElement>) => {
    const t = e.target as HTMLElement;
    const pos = t.dataset['celda'];
    if (!pos) return;
    const [filaTxt, colTxt] = pos.split(':');
    const fila = Number(filaTxt);
    const col = colTxt as Col;
    const l = p.lineas[fila];
    const c = COLS.indexOf(col);
    const mover = (df: number, dc: number) => {
      e.preventDefault();
      ir(fila + df, COLS[Math.max(0, Math.min(COLS.length - 1, c + dc))]!);
    };
    if (e.key === 'ArrowDown') mover(1, 0);
    else if (e.key === 'ArrowUp') mover(-1, 0);
    else if (e.key === 'ArrowRight') mover(0, 1);
    else if (e.key === 'ArrowLeft') mover(0, -1);
    else if (e.key === 'Home') mover(0, -c);
    else if (e.key === 'End') mover(0, COLS.length - 1 - c);
    else if (e.key === 'Enter') {
      e.preventDefault();
      if (editables.includes(col)) editar(fila, col);
      else if (col === 'producto' && l) p.alVerProducto?.(l.clave);
    } else if ((e.key === 'Delete' || e.key === 'Supr') && editable && l) {
      e.preventDefault();
      p.alQuitar(l.clave);
    } else if (
      /^[0-9]$/.test(e.key) &&
      !e.ctrlKey &&
      !e.altKey &&
      (col === 'cantidad' || col === 'descuento') &&
      editables.includes(col)
    ) {
      e.preventDefault();
      editar(fila, col, e.key);
    }
  };

  const celdaDe = (fila: number) => (col: Col) => ({
    tabIndex: -1,
    'data-celda': `${fila}:${col}`,
    'data-cursor': cursor.fila === fila && cursor.col === col && !editando ? 'true' : undefined,
    onFocus: (e: FocusEvent) => {
      if (e.target === e.currentTarget) setCursor({ fila, col });
    },
  });

  const nLineas = p.lineas.length + (p.soloLectura ? 0 : 1);
  return (
    <div
      className="grilla-nv"
      data-grilla="nota"
      data-modo={editando ? 'edicion' : 'navegacion'}
      onKeyDownCapture={(e) => {
        if (p.alTabulador && e.key === 'Tab' && !e.shiftKey && !e.altKey && !e.ctrlKey) {
          e.preventDefault();
          e.stopPropagation();
          (e.target as HTMLElement).blur?.();
          p.alTabulador();
        } else if (
          p.alTabularFinal &&
          e.key === 'Tab' &&
          !e.shiftKey &&
          !e.altKey &&
          !e.ctrlKey &&
          (e.target as HTMLElement).getAttribute('aria-label') === 'Agregar producto' &&
          !(e.target as HTMLInputElement).value.trim()
        ) {
          e.preventDefault();
          e.stopPropagation();
          p.alTabularFinal();
        }
      }}
    >
      <table className="grilla-c21" aria-label="Líneas de la nota">
        <colgroup>
          <col className="c-n" />
          <col className="c-cod" />
          <col />
          <col className="c-stock" />
          <col className="c-cant" />
          <col className="c-lista" />
          <col className="c-venta" />
          <col className="c-desc" />
          <col className="c-total" />
        </colgroup>
        <thead>
          <tr>
            <th>#</th>
            <th>Código</th>
            <th>Producto</th>
            <th className="al-der" title="Disponible en la bodega de la nota (unidad base)">
              Stock
            </th>
            <th
              className="al-der"
              title="Unidad base. Con caja: diálogo de unidades o cajas (R.T.U.); «5c» = 5 cajas"
            >
              Cantidad
            </th>
            <th className="al-der" title="Precio de lista, sin escala (solo lectura)">
              P. lista
            </th>
            <th className="al-der" title="Precio que se cobra (escala ≥tramo, lista o manual M)">
              P. venta
            </th>
            <th className="al-der">Desc.</th>
            <th className="al-der">Total</th>
          </tr>
        </thead>
        <tbody
          ref={cuerpo}
          onKeyDown={(e) => {
            if (!p.soloLectura && e.ctrlKey && e.key === 'Delete') {
              const fila =
                Number((e.target as HTMLElement).closest('tr')?.dataset['linea'] ?? 0) - 1;
              const l = p.lineas[fila];
              if (l) {
                e.preventDefault();
                p.alQuitar(l.clave);
                return;
              }
            }
            teclaNavegacion(e);
          }}
        >
          {p.lineas.map((l, i) => (
            <FilaLinea
              key={l.clave}
              p={p}
              l={l}
              i={i}
              activa={p.claveSeleccionada === l.clave}
              editando={editando?.fila === i ? editando.col : null}
              celda={celdaDe(i)}
              alEditar={(col) => {
                setCursor({ fila: i, col });
                setEditando({ fila: i, col });
                seleccionar(l.clave);
              }}
              alSoltar={(col) => setEditando((x) => (x?.fila === i && x.col === col ? null : x))}
              alSalir={(col) => ir(i, col)}
              alMover={(col, df) => ir(i + df, col)}
              alConfirmar={(col) => avanzar(i, col)}
              alPrecioManual={(precio) => setManual({ linea: l, ...(precio ? { precio } : {}) })}
            />
          ))}
          {!p.soloLectura && (
            <tr className="fila-digitacion">
              <td className="mono apagado">{p.lineas.length + 1}</td>
              <td colSpan={2}>
                <input
                  ref={agregar}
                  className="celda-codigo"
                  aria-label="Agregar producto"
                  placeholder={
                    p.puedeAgregar
                      ? 'código → Enter → cantidad → Enter · F3 buscar'
                      : 'Primero elija el cliente (F2)'
                  }
                  disabled={!p.puedeAgregar}
                  value={p.textoAgregar}
                  onFocus={() => setEditando(null)}
                  onChange={(e) => p.alCambiarTextoAgregar(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      e.stopPropagation();
                      p.alBuscarProducto(p.textoAgregar);
                    } else if (e.key === 'ArrowUp' && p.lineas.length) {
                      e.preventDefault();
                      e.stopPropagation();
                      ir(p.lineas.length - 1, cursor.col === 'codigo' ? 'codigo' : 'cantidad');
                    }
                  }}
                />
              </td>
              <td colSpan={6} />
            </tr>
          )}
          {Array.from({ length: Math.max(0, RELLENO - nLineas) }, (_, i) => (
            <tr key={`r${i}`} className="relleno" aria-hidden>
              <td colSpan={9} />
            </tr>
          ))}
        </tbody>
      </table>
      {manual && (
        <PrecioManualDialogo
          linea={manual.linea}
          {...(manual.precio ? { precioInicial: manual.precio } : {})}
          conAutorizacion={!!p.precioManualConAutorizacion}
          alCerrar={() => {
            const fila = p.lineas.findIndex((x) => x.clave === manual.linea.clave);
            setManual(null);
            if (fila >= 0) setTimeout(() => ir(fila, 'venta'), 0);
          }}
          alGuardar={(v) => {
            const fila = p.lineas.findIndex((x) => x.clave === manual.linea.clave);
            p.alCambiar(manual.linea.clave, { precioManual: v });
            setManual(null);
            // Aplicado el precio, sigue el flujo: descuento.
            if (fila >= 0) setTimeout(() => avanzar(fila, 'venta'), 0);
          }}
        />
      )}
    </div>
  );
});
